import React from "react";
import { useShallow } from "zustand/react/shallow";
import { Event } from "@uno/protocol";
import { AppContext } from "../../context";
import { EventHandler } from "../../types/Event";
import useStoreEvent from "../../store/useStoreEvent";
import useStoreAnimation from "../../store/useStoreAnimation";
import useRoomPlayerDrawCard from "./events/useRoomPlayerDrawCard";
import useRoomPlayerPuttingCard from "./events/useRoomPlayerPuttingCard";
import useRefreshRoomPlayerCard from "./events/useRefreshRoomPlayerCard";
import useRoomStartDistribution from "./events/useRoomStartDistribution";
import useRoomStateUpdate from "./events/useRoomStateUpdate";
import useRoomPlayerAction from "./events/useRoomPlayerAction";
import useRoomPlayersSwappedHands from "./events/useRoomPlayersSwappedHands";

const MAX_ATTEMPTS = 3;
const MAX_EVENTS_PER_PASS = 50;

const hasBlockingAnimation = (): boolean => {
    if(document.hidden) {
        return false;
    }

    return Object.values(useStoreAnimation.getState().animations).some(arr => arr.length > 0 && !arr.some(animation => animation.isAsynchronous));
};

const useRoomEvent = () => {
    const { socket } = React.useContext(AppContext);
    const { events, enqueue, requeue } = useStoreEvent(useShallow(state => ({ events: state.events, enqueue: state.enqueue, requeue: state.requeue })));
    const animations = useStoreAnimation(state => state.animations);
    const flushAnimations = useStoreAnimation(state => state.flush);

    const handlers: Record<string, EventHandler> = {
        [Event.RoomStateUpdate]: {
            handler: useRoomStateUpdate(),
            canBeStacked: false,
            type: "room"
        },
        [Event.RoomPlayerDrawnCard]: {
            handler: useRoomPlayerDrawCard(),
            canBeStacked: true,
            type: "room"
        },
        [Event.RoomPlayerPuttingCard]: {
            handler: useRoomPlayerPuttingCard(),
            canBeStacked: true,
            type: "room"
        },
        [Event.RefreshRoomPlayerCards]: {
            handler: useRefreshRoomPlayerCard(),
            canBeStacked: true,
            type: "room"
        },
        [Event.RoomStartDistribution]: {
            handler: useRoomStartDistribution(),
            canBeStacked: true,
            type: "room"
        },
        [Event.RoomPlayerAction]: {
            handler: useRoomPlayerAction(),
            canBeStacked: true,
            type: "room"
        },
        [Event.RoomPlayersSwappedHands]: {
            handler: useRoomPlayersSwappedHands(),
            canBeStacked: true,
            type: "room"
        }
    };
    const handlersRef = React.useRef(handlers);
    handlersRef.current = handlers;

    React.useEffect(() => {
        const store = useStoreEvent.getState();
        let drawOrder = 0;

        for(let pass = 0; pass < MAX_EVENTS_PER_PASS; pass++) {
            const next = store.peek("room");

            if(!next) {
                return;
            }

            const isDraw = next.name === Event.RoomPlayerDrawnCard;

            if(!(isDraw && drawOrder > 0) && hasBlockingAnimation()) {
                return;
            }

            const event = store.dequeue("room");

            if(!event) {
                return;
            }

            try {
                handlersRef.current[event.name]?.handler(event.payload, drawOrder);
            } catch(_error) {
                const attempts = (event.attempts ?? 0) + 1;

                if(attempts < MAX_ATTEMPTS) {
                    setTimeout(() => enqueue(event.name, event.type, event.payload, event.priority ?? "medium", attempts), 500);
                }
            }

            drawOrder = isDraw ? drawOrder + 1 : 0;
        }
    }, [socket, events, animations]);

    React.useEffect(() => {
        Object.keys(handlers).forEach(eventName => {
            socket?.on(eventName, (payload) => {
                const currentEvents = useStoreEvent.getState().events;
                const alreadyEnqueued = currentEvents.some(e => e.name === eventName);
                const handler = handlers[eventName as keyof typeof handlers];

                if(!handler) {
                    return console.error(`Handler for event ${eventName} not found`);
                }

                if(eventName === Event.RoomStateUpdate && payload) {
                    payload = { ...payload, receivedAt: Date.now() };
                }

                if(!alreadyEnqueued || handler.canBeStacked) {
                    return enqueue(eventName, handler.type, payload, handler.priority ?? "medium");
                }

                requeue(eventName, handler.type, payload, handler.priority ?? "medium");
            });
        });

        return () => {
            Object.keys(handlers).forEach(eventName => socket?.off(eventName));
        };
    }, [socket]);

    React.useEffect(() => {
        const onVisibilityChange = () => {
            if(!document.hidden) {
                flushAnimations();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, []);
};

export default useRoomEvent;
